import React, { lazy, Suspense, useMemo, useState } from 'react';
import { Flex } from '@world-vision/wv360-core-library';
import { graphql, Link } from 'gatsby';
import { ContentType, slugify } from '../../utility/text';

import { AEM_ASSETS_URL } from 'gatsby-env-variables';
import { RoutingNode } from '../../../gatsby-node';
import Seo, { Location } from '../Seo';
import { ImageType } from '../../types/common/aem';

const Article = lazy(() => import('@world-vision/wv360-core-library').then(module => ({ default: module.Article })));

type QuoteType = {
    quoteBody: ContentType;
};

type ArticleType = {
    _path: string;
    title: string;
    tags: string[];
    subHead: string | null;
    contentBody: ContentType[];
    images: Array<NonNullable<ImageType>>;
    DisplayDate: string;
    status: string;
    createdByAuthorDetails: {
        firstName: string;
        lastName: string;
    };
    pageName: string;
    metadescription: string;
    publisheddate: string;
    video: string;
    videoTitle: string;
    videoCoverImage: ImageType;
    quote: QuoteType[];
};

export type ArticleContextQuery = {
    datasource: {
        multiContextList: {
            items: ArticleType[];
        };
    };
};

export const query = graphql`
    query GetMultiContextByPath {
        datasource {
            multiContextList(
                filter: {
                    pageName: { _expressions: { value: null, _operator: EQUALS_NOT } },
                    tags: { _expressions: { value: "world-vision-canada:content-type/story-narrative", _operator: EQUALS} }
                }
            ) {
                items {
                    _path
                    title
                    tags
                    subHead
                    contentBody {
                        html
                    }
                    images {
                        ... on DATASOURCE_ImageRef {
                            title
                            description
                            _path
                        }
                    }
                    DisplayDate
                    createdByAuthorDetails {
                        firstName
                        lastName
                    }
                    pageName
                    metadescription
                    publisheddate
                    video
                    videoTitle
                    videoCoverImage {
                        ... on DATASOURCE_ImageRef {
                            _path
                        }
                    }
                    quote {
                        quoteBody {
                          html
                          plaintext
                        }
                    }
                }
            }
        }
    }
`;

type ArticleProps = {
    pageContext?: RoutingNode;
    data?: ArticleContextQuery,
    path?: string;
    params?: Record<string, string>;
};

const getAllArticles = (data: ArticleContextQuery | undefined): ArticleType[] => {
    const { datasource: { multiContextList: { items } } } = (data ?? {
        datasource: { multiContextList: { items: [] } }
    });
    return items ?? [];
}

const getArticles = (data: ArticleContextQuery | undefined) => {
    const items = getAllArticles(data);
    return items.reduce((map, article) => {
        const name = slugify(article?.pageName, '| World Vision Canada');
        if (name) {
            const key = `/${name}`;
            map[key] = article;
        }
        return map;
    }, {} as Record<string, ArticleType>);
};

const getArticle = (articles: Record<string, ArticleType>, name: string): ArticleType | undefined => {
    if (name) {
        const story = articles[`/${name}`];
        if (story) {
            return story;
        }
    }
}

type LangType = { lang: string };

type ArticleContext = {
    articles: Record<string, ArticleType>;
    article: ArticleType | undefined;
    name: string;
}

const useGetArticleDetails = (data: ArticleContextQuery | undefined, params: Record<string, string> = {}): ArticleContext => {
    const name = params?.['*'];
    const articles = useMemo(() => getArticles(data), [name]);
    const [article] = useState(() => getArticle(articles, name));
    return { articles, article, name };
};

const FallBack = ({ lang }: LangType) => (
    <Flex direction="column" wrap='wrap' justify="center" align="center" gridColumn="1" height="100vh">
        Oops! We couldn't find that article. It might have been moved or deleted. Explore our latest stories on the <Link to={`/${lang}/`}>Home page</Link>.
    </Flex>
)

const View = ({ articles, article, name, lang }: ArticleContext & LangType) => {
    if (articles && name) {
        if (article) {
            return (
                <Suspense fallback={<div>Loading article...</div>}>
                    <Article
                        article={article}
                        asset={{
                            prefix: AEM_ASSETS_URL,
                        }}
                    />
                </Suspense>
            );
        }
        return <FallBack lang={lang} />;
    }
    return null;
};

export const Story = ({ data, params }: ArticleProps) => {
    const lang = "en";
    const { articles, article, name } = useGetArticleDetails(data, params);

    return (
        <View articles={articles} article={article} name={name} lang={lang} />
    );
};

type HeadProps = ArticleProps & Location;
export const Head = ({ data, pageContext, params, location: { pathname } }: HeadProps) => {
    const { article } = useGetArticleDetails(data, params);
    console.log('Head article', article);
    console.log('pageContext', pageContext);

    if (!article) {
        console.log('No article found');
        return null;
    }

    const title = article.pageName ?? pageContext?.label;
    const description = article.metadescription;
    const image = article.images?.[0]?._path;
    return (
        <Seo title={title} description={description} image={image} pathName={pathname} />
    )
}

export default Story;